/**
 * Ezoic Ads Integration for React SPA
 *
 * MODALITA 100% DINAMICA:
 * - Ezoic AI scansiona la pagina e decide AUTOMATICAMENTE dove inserire ads
 * - NESSUN placeholder forzato
 * - NESSUN CMP Ezoic (usa il cookie banner esistente del sito)
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  memo
} from 'react';
import { useLocation } from 'react-router-dom';

// ==================== CONTEXT ====================

const EzoicContext = createContext({
  isLoaded: false,
  isEnabled: false,
  hasConsent: false,
  refresh: () => {}
});

export const useEzoic = () => useContext(EzoicContext);

// ==================== PROVIDER COMPONENT ====================

/**
 * EzoicProvider - Gestisce Ezoic in modalità COMPLETAMENTE DINAMICA
 *
 * - NO CMP Ezoic (usa il tuo cookie banner)
 * - NO placeholder fissi
 * - Ezoic AI decide TUTTO automaticamente
 */
export const EzoicProvider = memo(({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const initAttempts = useRef(0);

  // Check consent from YOUR cookie banner (localStorage)
  useEffect(() => {
    const checkConsent = () => {
      try {
        const prefs = localStorage.getItem('cookie_preferences');
        if (prefs) {
          const preferences = JSON.parse(prefs);
          setHasConsent(preferences.advertising === true);
        }
      } catch (e) {
        console.error('Error checking ad consent:', e);
      }
    };

    checkConsent();

    // Listen for consent changes
    const handleStorageChange = (e) => {
      if (e.key === 'cookie_preferences') {
        checkConsent();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('consentUpdated', checkConsent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('consentUpdated', checkConsent);
    };
  }, []);

  // Load Ezoic script when consent is granted
  useEffect(() => {
    if (!hasConsent) return;

    // Already loaded
    if (window.ezstandalone) {
      setIsLoaded(true);
      return;
    }

    // Check if script already exists (loaded from index.html)
    if (document.querySelector('script[src*="ezojs.com/ezoic/sa.min.js"]')) {
      const checkLoaded = setInterval(() => {
        if (window.ezstandalone) {
          setIsLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      setTimeout(() => clearInterval(checkLoaded), 10000);
      return;
    }

    // Load Ezoic standalone script (NO CMP)
    const script = document.createElement('script');
    script.src = 'https://ezojs.com/ezoic/sa.min.js';
    script.async = true;

    script.onload = () => setIsLoaded(true);
    script.onerror = () => console.error('Failed to load Ezoic script');

    document.head.appendChild(script);
  }, [hasConsent]);

  // Initialize Ezoic DYNAMIC mode
  const initializeEzoic = useCallback(() => {
    if (!isLoaded || !hasConsent) return;

    const tryInit = () => {
      try {
        const ez = window.ezstandalone;

        if (!ez) {
          initAttempts.current++;
          if (initAttempts.current < 20) {
            setTimeout(tryInit, 250);
          }
          return;
        }

        ez.cmd = ez.cmd || [];

        ez.cmd.push(function() {
          // DYNAMIC: no define(), Ezoic AI decides everything
          if (!ez.enabled) {
            ez.enable();
            ez.display();
          }
        });

        setIsEnabled(true);
      } catch (error) {
        console.error('Error initializing Ezoic:', error);
        initAttempts.current++;
        if (initAttempts.current < 20) {
          setTimeout(tryInit, 250);
        }
      }
    };

    tryInit();
  }, [isLoaded, hasConsent]);

  useEffect(() => {
    if (isLoaded && hasConsent) {
      initializeEzoic();
    }
  }, [isLoaded, hasConsent, initializeEzoic]);

  // Handle SPA navigation - refresh for dynamic re-scan
  useEffect(() => {
    if (!isEnabled || !hasConsent) return;
    if (previousPathRef.current === location.pathname) return;
    previousPathRef.current = location.pathname;

    const refreshAds = () => {
      try {
        const ez = window.ezstandalone;
        if (ez && ez.enabled) {
          ez.cmd = ez.cmd || [];
          // Delay to let React render new content
          setTimeout(() => {
            ez.cmd.push(function() {
              ez.refresh();
            });
          }, 500);
        }
      } catch (error) {
        console.error('Error refreshing Ezoic:', error);
      }
    };

    refreshAds();
  }, [location.pathname, isEnabled, hasConsent]);

  // Manual refresh (exposed via hook)
  const refresh = useCallback(() => {
    try {
      const ez = window.ezstandalone;
      if (ez && ez.enabled) {
        ez.cmd = ez.cmd || [];
        ez.cmd.push(function() {
          ez.refresh();
        });
      }
    } catch (error) {
      console.error('Error refreshing Ezoic:', error);
    }
  }, []);

  return (
    <EzoicContext.Provider value={{ isLoaded, isEnabled, hasConsent, refresh }}>
      {children}
    </EzoicContext.Provider>
  );
});

EzoicProvider.displayName = 'EzoicProvider';

// ==================== PLACEHOLDER COMPONENTS (VUOTI) ====================
// Manteniamo i componenti per non rompere gli import esistenti,
// ma NON renderizzano nulla - Ezoic AI decide tutto

export const EzoicAfterCalculator = memo(() => null);
export const EzoicAfterFAQ = memo(() => null);
export const EzoicBetweenSections = memo(() => null);
export const EzoicSidebar = memo(() => null);
export const EzoicInContent = memo(() => null);
export const EzoicInArticle = memo(() => null);

EzoicAfterCalculator.displayName = 'EzoicAfterCalculator';
EzoicAfterFAQ.displayName = 'EzoicAfterFAQ';
EzoicBetweenSections.displayName = 'EzoicBetweenSections';
EzoicSidebar.displayName = 'EzoicSidebar';
EzoicInContent.displayName = 'EzoicInContent';
EzoicInArticle.displayName = 'EzoicInArticle';

export default EzoicProvider;
